import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useLocation } from '../../../contexts/LocationContext';
import { useBusinessTypeContext } from '../../../contexts/BusinessTypeContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useMutation } from '../../../hooks/useApi';
import LoadingSpinner from '../../../components/UI/LoadingSpinner';
import apiClient, { getErrorMessage } from '../../../utils/api';
import { GoatShed } from '../../../types/api';

interface GoatShedFormData {
  name: string;
  description?: string;
  capacity?: number;
  area_sqft?: number;
  is_active: boolean;
}

const shedSchema = yup.object({
  name: yup.string().required('Shed name is required'),
  description: yup.string().optional(),
  capacity: yup.number().min(1).optional(),
  area_sqft: yup.number().min(0).optional(),
  is_active: yup.boolean().required(),
});

const GoatShedForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { selectedLocation } = useLocation();
  const { businessType } = useBusinessTypeContext();
  const { user } = useAuth();
  const [loading, setLoading] = useState(!!id);
  const [error, setError] = useState('');

  // Redirect non-admin users away from edit page
  useEffect(() => {
    if (id && user?.role !== 'admin') {
      navigate('/goat/sheds');
    }
  }, [id, user?.role, navigate]);

  const { mutate: createShed, loading: creating } = useMutation<GoatShed, GoatShedFormData>();
  const { mutate: updateShed, loading: updating } = useMutation<GoatShed, GoatShedFormData>();

  const form = useForm<GoatShedFormData>({
    resolver: yupResolver(shedSchema) as any,
    defaultValues: {
      name: '',
      description: '',
      capacity: undefined,
      area_sqft: undefined,
      is_active: true,
    },
  });

  useEffect(() => {
    if (businessType !== 'goat' || !selectedLocation?.id) return;

    if (id) {
      loadShedData();
    }
  }, [id, selectedLocation?.id, businessType]);

  const loadShedData = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/goat/sheds/${id}`);
      const shed = response.data;
      
      // Convert square meters to square feet (1 sq m = 10.7639 sq ft)
      const areaSqft = shed.size_sqm ? Math.round(shed.size_sqm * 10.7639 * 100) / 100 : undefined;
      
      form.reset({
        name: shed.name,
        description: shed.description || '',
        capacity: shed.capacity,
        area_sqft: areaSqft,
        is_active: shed.is_active,
      });
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to load shed data'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (data: GoatShedFormData) => {
    try {
      setError('');
      
      // Convert square feet to square meters (1 sq ft = 0.092903 sq m)
      const sizeSqm = data.area_sqft ? data.area_sqft * 0.092903 : undefined;
      
      const shedData: any = {
        name: data.name,
        description: data.description,
        capacity: data.capacity,
        size_sqm: sizeSqm,
        is_active: data.is_active,
        location_id: selectedLocation?.id,
      };

      if (id) {
        await updateShed(`/goat/sheds/${id}`, shedData, 'PUT');
      } else {
        await createShed('/goat/sheds', shedData);
      }
      
      navigate('/goat/sheds');
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to save shed'));
    }
  };

  if (businessType !== 'goat') {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">This form is only available for goat farming locations.</p>
        <Link to="/dashboard" className="text-blue-600 hover:text-blue-800">Go to Dashboard</Link>
      </div>
    );
  }

  if (!selectedLocation) {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">Go to Location Selection</Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container-mobile py-8">
        <div className="flex justify-center">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link to="/goat/sheds" className="btn-touch p-2 text-gray-400 hover:text-gray-600">
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {id ? 'Edit' : 'Add'} Goat Shed
          </h1>
          <p className="text-gray-600">{selectedLocation.name}</p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Form */}
      <div className="card p-6">
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="form-label">Shed Name *</label>
              <input
                type="text"
                {...form.register('name')}
                className="form-input"
                placeholder="e.g., Shed A, Main Shed"
              />
              {form.formState.errors.name && (
                <p className="mt-1 text-sm text-red-600">{form.formState.errors.name.message}</p>
              )}
            </div>

            <div>
              <label className="form-label">Capacity (goats)</label>
              <input
                type="number"
                min="1"
                {...form.register('capacity')}
                className="form-input"
                placeholder="Optional"
              />
            </div>

            <div>
              <label className="form-label">Area (sq ft)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                {...form.register('area_sqft')}
                className="form-input"
                placeholder="Optional"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="form-label">Description</label>
              <textarea
                {...form.register('description')}
                className="form-input"
                rows={3}
                placeholder="Optional description"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  {...form.register('is_active')}
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-gray-700">Active</span>
              </label>
            </div>
          </div>

          <div className="flex space-x-4 pt-4">
            <button
              type="submit"
              disabled={creating || updating}
              className="flex-1 btn-touch bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center"
            >
              {(creating || updating) ? <LoadingSpinner size="sm" /> : (id ? 'Update' : 'Create')}
            </button>
            <Link
              to="/goat/sheds"
              className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GoatShedForm;
